import {addQueryToPath} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {areUnorderedEqual} from '../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {CancelError, CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../libs/shared/core/src/services/http/httpError';
import {DropdownFetcher} from '../../components/common/dropdowns/source/asyncDropdownSource';
import {AnalyticsChannelModel, AnalyticsInboxModel} from '../../models/api/analyticsFilterResourcesModel';
import {ApiAnalyticsSearchChannels} from '../../models/api/analyticsSearchChannelsModel';
import {ApiAnalyticsSearchInboxes} from '../../models/api/analyticsSearchInboxesModel';
import {makeAnalyticsApiPath} from '../../store/panels/analytics/analyticsPanelHelpers';

/*
 * Analytics Channels fetcher.
 */

const fetchChannelsLimit = 25;

interface ChannelsFilterPaginationData {
  offset?: number;
  teamNamespaces: ReadonlyArray<string>;
}

export function makeChannelsFetcher(
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
  onChannelsFetched: (channels: ReadonlyArray<AnalyticsChannelModel>) => void,
): DropdownFetcher<AnalyticsChannelModel, ChannelsFilterPaginationData> {
  return async (
    query: string,
    channels: ReadonlyArray<AnalyticsChannelModel>,
    cancelToken: CancelToken,
    paginationData?: ChannelsFilterPaginationData,
    filterIds?: ReadonlyArray<number>,
  ) => {
    const selectedTeamNamespacesArray = [...selectedTeamNamespaces];
    const sameTeamNamespaces = areUnorderedEqual(
      paginationData?.teamNamespaces ?? [],
      selectedTeamNamespacesArray,
    );

    // reset offset if team namespaces changed
    const offset = sameTeamNamespaces ? paginationData?.offset ?? 0 : 0;

    const fetchChannelsUrl = makeSearchChannelsOrInboxesUrl(company, {
      namespace,
      query: query.length > 2 ? query : undefined,
      teamNamespaces: selectedTeamNamespacesArray,
      limit: fetchChannelsLimit,
      extraFilterType: 'channel_ids',
      filterIds,
      offset,
      resourceType: 'channels',
    });

    try {
      const response = await fetchApiAsync(ApiAnalyticsSearchChannels, fetchChannelsUrl, {cancelToken});
      cancelToken.throwIfRequested();

      const newOffset = (offset ?? 0) + response.limit;

      onChannelsFetched(response.channels);

      return {
        items: channels.concat(response.channels),
        canLoadMore: response.channels.length > 0 && response.limit < response.total,
        paginationData: {
          offset: newOffset,
          teamNamespaces: selectedTeamNamespacesArray,
        },
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/*
 * Analytics Inboxes fetcher.
 */

const fetchInboxesLimit = 25;

interface InboxesFilterPaginationData {
  offset?: number;
  teamNamespaces: ReadonlyArray<string>;
}

export function makeInboxesFetcher(
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
  onInboxedFetched: (inboxes: ReadonlyArray<AnalyticsInboxModel>) => void,
): DropdownFetcher<AnalyticsInboxModel, InboxesFilterPaginationData> {
  return async (
    query: string,
    inboxes: ReadonlyArray<AnalyticsInboxModel>,
    cancelToken: CancelToken,
    paginationData?: InboxesFilterPaginationData,
    filterIds?: ReadonlyArray<number>,
  ) => {
    const selectedTeamNamespacesArray = [...selectedTeamNamespaces];
    const sameTeamNamespaces = areUnorderedEqual(
      paginationData?.teamNamespaces ?? [],
      selectedTeamNamespacesArray,
    );

    const offset = sameTeamNamespaces ? paginationData?.offset ?? 0 : 0;

    const fetchInboxesUrl = makeSearchChannelsOrInboxesUrl(company, {
      namespace,
      query: query.length > 2 ? query : undefined,
      teamNamespaces: selectedTeamNamespacesArray,
      limit: fetchInboxesLimit,
      extraFilterType: 'inbox_ids',
      filterIds,
      // reset offset if team namespaces changed
      offset,
      resourceType: 'inboxes',
    });

    try {
      const response = await fetchApiAsync(ApiAnalyticsSearchInboxes, fetchInboxesUrl, {cancelToken});
      cancelToken.throwIfRequested();

      const newOffset = (offset ?? 0) + response.limit;

      onInboxedFetched(response.inboxes);

      return {
        items: inboxes.concat(response.inboxes),
        canLoadMore: response.inboxes.length > 0 && response.limit < response.total,
        paginationData: {
          offset: newOffset,
          teamNamespaces: selectedTeamNamespacesArray,
        },
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

interface MakeSearchChannelsOrInboxesUrlOptions {
  namespace: string;
  limit: number;
  offset: number;
  teamNamespaces: ReadonlyArray<string>;
  filterIds?: ReadonlyArray<number>;
  extraFilterType: 'inbox_ids' | 'channel_ids';
  query?: string;
  resourceType: 'channels' | 'inboxes';
}

function makeSearchChannelsOrInboxesUrl(
  company: CompanyModel,
  options: MakeSearchChannelsOrInboxesUrlOptions,
) {
  return addQueryToPath(
    `${makeAnalyticsApiPath(company.url, options.namespace)}/search_${
      options.resourceType
    }/${encodeURIComponent(options?.query ?? '')}`,
    {
      limit: options?.limit,
      offset: options?.offset,
      team_namespaces: options.teamNamespaces.length === 0 ? undefined : options.teamNamespaces,
      [options.extraFilterType]: options.filterIds?.length ? options.filterIds.join(',') : undefined,
    },
  );
}
